import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import BSL_Dialog from "../../components/BSL_Dialog";
import { updateSiteParameters } from "../../redux_store/jobState";
import MessageSupport from "../../components_designer/main_drawer/MessageSupport";

const dateTimeOptions = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
};

export default function NonStandardDeliveryDialog({ area }) {
    const dispatch = useDispatch();
    const [currentRecord, setCurrentRecord] = useState(null);
    const [dismissMessage, setDismissMessage] = useState(false);
    const [ackEmailSent, setAckEmailSent] = useState(false);
    const [showEmailSendError, setShowEmailSendError] = useState(false);
    const [openEmailDialog, setOpenEmailDialog] = useState(false);
    const [checkDidFail, setCheckDidFail] = useState(false);
    const [supportEmailText, setSupportEmailText] = useState("");

    const userId = useSelector((state) => state.app.user.id);

    const emailOutcomeRef = useRef("idle");

    useEffect(() => {
        const currentId = area?.CurrentId;

        if (!area?.AreaHistory?.length || !currentId) {
            setCurrentRecord(null);
            return;
        }

        if (currentId === "error") {
            setCurrentRecord(null);
            setCheckDidFail(true);
            return;
        }

        const currentHistoryItem = area.AreaHistory.find((h) => h.Id === currentId) || null;
        setCurrentRecord(currentHistoryItem);
    }, [area]);

    useEffect(() => {
        if (!currentRecord) return;
        const suburb = currentRecord?.Town || "unknown";
        const state = currentRecord?.State || "unknown";
        const postcode = currentRecord?.PostCode || "unknown";

        const emailText =
            `Please confirm the fee to be charged for delivery to this address which falls ` +
            `outside the standard delivery area:\n\n ${suburb} \n ${state} \n ${postcode}`;

        setSupportEmailText(emailText);
    }, [currentRecord]);

    function handleClickSendLater(dets) {
        deferEmail(dets);
    }

    function deferEmail(dets) {
        if (!dets) return;

        setDismissMessage(true);

        const now = Date.now();
        const updatedNonStdDelivery = {
            ...dets,
            AreaHistory: dets.AreaHistory.map((h) =>
                h.Id === dets.CurrentId
                    ? {
                          ...h,
                          EmailDeferHistory: [
                              ...(h.EmailDeferHistory ?? []),
                              {
                                  timestamp: now,
                                  user: userId,
                              },
                          ],
                      }
                    : h
            ),
        };

        dispatch(updateSiteParameters("NonStandardDeliveryArea", updatedNonStdDelivery));
    }

    function handleClickSendEmail() {
        setOpenEmailDialog(true);
        setDismissMessage(true);
    }

    function handleClickAckEmailSent() {
        setAckEmailSent(true);
    }

    function onSendEmailSuccess(result) {
        if (!result) return;

        emailOutcomeRef.current = "success";

        const now = Date.now();
        const updatedNonStdDelivery = {
            ...area,
            AreaHistory: area.AreaHistory.map((h) =>
                h.Id === area.CurrentId
                    ? {
                          ...h,
                          EmailSentSuccess: [
                              ...(h.EmailSentSuccess ?? []),
                              {
                                  timestamp: now,
                                  user: userId,
                                  to: result?.to || "unknown",
                                  content: result?.content || "none",
                              },
                          ],
                      }
                    : h
            ),
        };

        dispatch(updateSiteParameters("NonStandardDeliveryArea", updatedNonStdDelivery));
    }

    function onSendEmailError(result) {
        if (!result) return;

        emailOutcomeRef.current = "error";

        const now = Date.now();
        const updatedNonStdDelivery = {
            ...area,
            AreaHistory: area.AreaHistory.map((h) =>
                h.Id === area.CurrentId
                    ? {
                          ...h,
                          EmailSentError: [
                              ...(h.EmailSentError ?? []),
                              {
                                  timestamp: now,
                                  user: userId,
                                  to: result?.to || "unknown",
                                  content: result?.content || "none",
                                  msg: result?.msg || "unknown",
                              },
                          ],
                      }
                    : h
            ),
        };

        dispatch(updateSiteParameters("NonStandardDeliveryArea", updatedNonStdDelivery));
        setShowEmailSendError(true);
    }

    function setOpenEmailDialogTracked(next) {
        const nextVal = typeof next === "function" ? next(openEmailDialog) : next;

        if (openEmailDialog === true && nextVal === false) {
            if (emailOutcomeRef.current === "idle") {
                deferEmail(area);
            }

            emailOutcomeRef.current = "idle";
        }

        setOpenEmailDialog(nextVal);
    }

    return (
        <>
            {(() => {
                if (currentRecord?.AcknowledgedAt && !currentRecord?.EmailSentSuccess?.length > 0 && !dismissMessage) {
                    return (
                        <BSL_Dialog
                            title="Email for Delivery Charge"
                            open={true}
                            onClose={() => handleClickSendLater(area)}
                            button1Text="Send Now"
                            button1Click={() => handleClickSendEmail()}
                            button2Text="Send Later"
                            button2Click={() => handleClickSendLater(area)}
                        >
                            <div style={{ display: "flex", flexDirection: "column" }}>
                                <div>
                                    You have acknowledged this job is outside our standard delivery area and will incur
                                    an extra charge.
                                </div>
                                <br />
                                <div>Would you like to send your order manager an email to confirm the details?</div>
                            </div>
                        </BSL_Dialog>
                    );
                }

                if (checkDidFail && !currentRecord && !dismissMessage) {
                    return (
                        <BSL_Dialog
                            title="Unable to Determine Delivery Area"
                            open={true}
                            onClose={() => setDismissMessage(true)}
                            button1Text="OK"
                            button1Click={() => setDismissMessage(true)}
                        >
                            <div style={{ display: "flex", flexDirection: "column" }}>
                                <div>
                                    We were unable to determine if this job falls within a standard delivery area.
                                </div>
                                <br />
                                <div>Please go to <i>Edit Details</i> to try again.</div>
                            </div>
                        </BSL_Dialog>
                    );
                }

                if (
                    currentRecord?.AcknowledgedAt &&
                    (currentRecord?.EmailSentSuccess?.length ?? 0) > 0 &&
                    !ackEmailSent &&
                    !dismissMessage
                ) {
                    return (
                        <BSL_Dialog
                            title="Non-Standard Delivery Fee"
                            open={true}
                            onClose={() => handleClickAckEmailSent()}
                            button1Text="OK"
                            button1Click={() => handleClickAckEmailSent()}
                        >
                            <div style={{ display: "flex", flexDirection: "column" }}>
                                <div>
                                    You have acknowledged this job falls outside our standard delivery area and will
                                    incur an extra charge.
                                </div>
                                <br />
                                <div>
                                    An email has been sent to your order manager requesting confirmation of the delivery
                                    charge.
                                </div>
                                <br />
                                <div>
                                    <p style={{ margin: "0 0 0 10px" }}>
                                        <span style={{ marginRight: "5px", fontWeight: 600 }}>Sent by:</span>
                                        <span>{currentRecord.EmailSentSuccess?.[0]?.user || "unknown"}</span>
                                    </p>
                                    <p style={{ margin: "0 0 0 10px" }}>
                                        <span style={{ marginRight: "5px", fontWeight: 600 }}>To:</span>
                                        <span>{currentRecord.EmailSentSuccess?.[0]?.to || "unknown"}</span>
                                    </p>
                                    <p style={{ margin: "0 0 0 10px" }}>
                                        <span style={{ marginRight: "5px", fontWeight: 600 }}>At:</span>
                                        <span>
                                            {new Date(currentRecord.EmailSentSuccess?.[0]?.timestamp).toLocaleString(
                                                undefined,
                                                dateTimeOptions
                                            )}
                                        </span>
                                    </p>
                                </div>
                            </div>
                        </BSL_Dialog>
                    );
                }

                if (showEmailSendError && !dismissMessage) {
                    return (
                        <BSL_Dialog
                            title="Sorry, Something Went Wrong"
                            open={true}
                            onClose={() => setDismissMessage(true)}
                            button1Text="OK"
                            button1Click={() => setDismissMessage(true)}
                        >
                            <div style={{ display: "flex", flexDirection: "column" }}>
                                <div>We're unable to send your email at this time.</div>
                                <br />
                                <div>
                                    Please try again later or contact your order manager directly to confirm the
                                    delivery charge.
                                </div>
                            </div>
                        </BSL_Dialog>
                    );
                }

                if (openEmailDialog) {
                    return (
                        <MessageSupport
                            style={{ display: openEmailDialog ? "flex" : "none" }}
                            setShowMessageSupport={setOpenEmailDialogTracked}
                            onSendSuccess={(res) => {
                                onSendEmailSuccess(res);
                            }}
                            onSendError={(res) => {
                                onSendEmailError(res);
                            }}
                            messageType={"FREIGHT POA"}
                            defaultMessage={supportEmailText}
                            subject={"Non-Standard Delivery Charge Confirmation"}
                        />
                    );
                }
            })()}
        </>
    );
}
